// == Import npm
import React, { useState, useEffect, useRef } from "react";
import { Avatar, Button, Input, Divider, Typography } from "antd";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { clone } from "ramda";
import classNames from "classnames";
import { useHistory } from "react-router-dom";

// == Import SCSS
import "./meetingPage.less";

import { ReactComponent as Chat } from "assets/illustrations/chat.svg";

// == Import component
import ListMeeting from "./ListMeeting/ListMeeting";
import ModalUpdate from "./ModalUpdate/ModalUpdate";
import ModalAdd from "./ModalAdd/ModalAdd";
import ModalPrint from "../ModalPrint/ModalPrint";
import { visitorMatchSearch } from "helpers";

// == Import icons
import { IoMdCalendar } from "react-icons/io";
import { MdPrint, MdNoteAdd } from "react-icons/md";

// == import i18n
import { useTranslation } from "react-i18next";
import { stopFeching, updateVisitsFromUser } from "services/features/VisitsSlice";

const MeetingPage = () => {
  const [t] = useTranslation();

  const history = useHistory();
  const dispatch = useDispatch();

  const typeVisitor = history.location.state.type;

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isModalAddVisible, setIsModalAddVisible] = useState(false);
  const [isModalPrintVisible, setIsModalPrintVisible] = useState(false);

  const [searchVisitors, setSearchVisitors] = useState("");
  const [visitorCheck, setVisitorCheck] = useState([]);
  const [displayButton, setDisplayButton] = useState(false);

  const [groupedVisits, setGroupedVisits] = useState([]);
  const [passedVisits, setPassedVisits] = useState([]);

  const { currentResident, currentVisits, disablePrinter, useKeyCard } = useSelector(
    state => state.visits,
  );
  const isModalPrintVisiblePrev = useRef(false);

  useEffect(() => {
    if (history.location.state.printVisitBadgeLost) {
      setVisitorCheck([
        {
          ...currentResident,
          visitId: history.location.state.printVisitBadgeLost,
          currentResident,
        },
      ]);
      showModalPrint();
    }
    if (history.location.state.knownVisit) {
      setVisitorCheck([
        {
          ...history.location.state.currentVisitor,
          visitId: history.location.state.knownVisit.id,
          resident: currentResident,
        },
      ]);
      showModalPrint();
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // on vient d'imprimer et on vient de la home. go back
    if (
      !isModalPrintVisible &&
      isModalPrintVisiblePrev.current === true &&
      history.location.state.goBackToHome
    ) {
      isModalPrintVisiblePrev.current = false;
      return history.push("/");
    }

    // on vient d'imprimer et on doit rester sur la page, on actualise les staturs (visites)
    if (!isModalPrintVisible && isModalPrintVisiblePrev.current === true) {
      isModalPrintVisiblePrev.current = false;
      dispatch(updateVisitsFromUser(currentResident));
    }

    if (isModalPrintVisible) {
      isModalPrintVisiblePrev.current = true;
    }
    // eslint-disable-next-line
  }, [history, isModalPrintVisible]);

  useEffect(() => {
    const groupedVisits = clone(
      currentVisits
        .filter(v => moment(v.endDate).isAfter(moment()))
        .filter(v => !(v.visitors.length === 1 && v.visitors[0].id === v.resident.id))
        .filter(
          v =>
            searchVisitors.length === 0 ||
            v.visitors.findIndex(vis => visitorMatchSearch(vis, searchVisitors)) > -1,
        ),
    );

    const passedVisits = clone(
      currentVisits
        .filter(v => moment(v.endDate).isBefore(moment()))
        .filter(v => !(v.visitors.length === 1 && v.visitors[0].id === v.resident.id))
        .filter(
          v =>
            searchVisitors.length === 0 ||
            v.visitors.findIndex(vis => visitorMatchSearch(vis, searchVisitors)) > -1,
        ),
    );

    setGroupedVisits(groupedVisits);
    setPassedVisits(passedVisits);
  }, [currentVisits, searchVisitors]);

  const { Search } = Input;

  const showModalPrint = () => {
    setIsModalPrintVisible(true);
  };

  const showModalAdd = () => {
    setIsModalAddVisible(true);
  };

  const date = new Date();

  const handleSearch = evt => {
    setSearchVisitors(evt.target.value);
    setVisitorCheck([]);
  };

  const handleCheckVisitor = (checked, visitor, visitId, resident) => {
    if (checked) {
      setVisitorCheck([...visitorCheck, { ...visitor, visitId, resident }]);
    } else {
      setVisitorCheck(visitorCheck.filter(v => !(v.id === visitor.id && visitId === v.visitId)));
    }
  };

  return (
    <div className="meetingPage">
      <ModalUpdate
        isVisible={isModalVisible}
        setVisible={setIsModalVisible}
        visitorCheck={visitorCheck}
        typeVisitor={typeVisitor}
        setVisitorCheck={setVisitorCheck}
      />
      {isModalAddVisible && (
        <ModalAdd
          isVisible={isModalAddVisible}
          setVisible={setIsModalAddVisible}
          user={currentResident}
          setSearch={setSearchVisitors}
          setIsModalPrintVisible={setIsModalPrintVisible}
          visitorCheck={visitorCheck}
          setVisitorCheck={setVisitorCheck}
          initialSearch={searchVisitors}
        />
      )}

      <ModalPrint
        isVisible={isModalPrintVisible}
        setVisible={setIsModalPrintVisible}
        visitorCheck={visitorCheck}
        setSearch={setSearchVisitors}
        setVisitorCheck={setVisitorCheck}
        mustCheckIn={true}
        disablePrinter={disablePrinter}
        useKeyCard={useKeyCard}
      />

      {!history.location.state.printVisitBadgeLost && !history.location.state.knownVisit && (
        <div className="meetingPage__texte">
          <Chat />
          <p>
            {t("WhatIsYour")} <br /> {t("NamePlease")} ?
          </p>
        </div>
      )}

      <div className="meetingPage__resident">
        <Typography.Title level={4}>{t("YouHaveAnAppointmentWith")}</Typography.Title>
        <div className="meetingPage__resident__avatar">
          <Avatar size={80} onError={() => {}} src={""}>
            {currentResident?.firstname.charAt(0)}
            {currentResident?.lastname.charAt(0)}
          </Avatar>
          <div className="meetingPage__resident__avatar__description">
            <Typography.Paragraph strong style={{ fontSize: "20px", margin: "0 0 0 10px" }}>
              {currentResident?.firstname || ""} {(currentResident?.lastname || "").toUpperCase()}
            </Typography.Paragraph>
            <Typography.Paragraph style={{ fontSize: "20px", margin: "0 0 0 10px" }}>
              {currentResident?.function}
            </Typography.Paragraph>
          </div>
        </div>
      </div>

      <section className="meetingPage__section">
        <Search
          style={{ width: "320px", margin: "0" }}
          placeholder="Rechercher un visiteur"
          onChange={handleSearch}
          value={searchVisitors}
        />
      </section>

      <div className="meetingPage__listMeeting">
        <div className="meetingPage__listMeeting__div">
          <Typography.Paragraph style={{ margin: "30px 0 0 40px", fontSize: "20px" }}>
            {date.getDate()}/{date.getMonth() + 1}/{date.getFullYear()}{" "}
            <IoMdCalendar style={{ fontSize: "27px", verticalAlign: "-5px" }} />
          </Typography.Paragraph>

          <div
            className={classNames("meetingPage__listMeeting__div__button", {
              "meetingPage__listMeeting__div__button--hide": !displayButton,
            })}
          >
            <Button
              type="primary"
              size="large"
              style={{
                width: "250px",
                fontWeight: "bold",
                marginRight: "10px",
                display: "flex",
              }}
              icon={<MdPrint style={{ marginRight: "10px" }} />}
              onClick={() => {
                showModalPrint();
                window.scrollTo(0, 0);
              }}
            >
              {visitorCheck.length > 1
                ? t(disablePrinter ? "GiveBadges" : "PrintBadges")
                : t(disablePrinter ? "GiveBadge" : "PrintBadge")}
            </Button>
          </div>
        </div>

        {groupedVisits.map((group, idx) => (
          <ListMeeting
            key={idx}
            handleCheckVisitor={handleCheckVisitor}
            visitorCheck={visitorCheck}
            visits={group}
            setDisplayButton={setDisplayButton}
            displayButton={displayButton}
            user={currentResident}
            setSearch={setSearchVisitors}
            setVisitorCheck={setVisitorCheck}
            initialSearch={searchVisitors}
            currentResident={currentResident}
            searchVisitors={searchVisitors}
          />
        ))}
        {passedVisits.length > 0 ? <Divider>{t("PassedVisits")}</Divider> : null}

        {passedVisits.map(group => (
          <ListMeeting
            handleCheckVisitor={handleCheckVisitor}
            visitorCheck={visitorCheck}
            visits={group}
            setDisplayButton={setDisplayButton}
            displayButton={true}
            user={currentResident}
            setSearch={setSearchVisitors}
            setVisitorCheck={setVisitorCheck}
            initialSearch={searchVisitors}
            currentResident={currentResident}
            searchVisitors={searchVisitors}
          />
        ))}
        {currentVisits.length === 0 ? (
          <Typography.Paragraph style={{ textAlign: "center", fontSize: "20px" }}>
            {t("NoVisitDay")} <br /> {currentResident?.lastname} {currentResident?.firstname}
          </Typography.Paragraph>
        ) : groupedVisits.length === 0 && passedVisits.length === 0 ? (
          <Typography.Paragraph style={{ textAlign: "center", fontSize: "20px" }}>
            {t("NoVisitsDay")} <br /> {currentResident?.lastname} {currentResident?.firstname}
          </Typography.Paragraph>
        ) : null}

        <Button
          onClick={() => {
            showModalAdd();
            window.scrollTo(0, 0);
          }}
          icon={<MdNoteAdd style={{ marginRight: "10px" }} />}
          type="primary"
          size="large"
          style={{
            width: "300px",
            fontWeight: "bold",
            margin: "30px auto",
            display: "flex",
          }}
        >
          {t("AddAVisit")}
        </Button>
      </div>
    </div>
  );
};

export default MeetingPage;
